/**
 * Reusable client-side pagination hook for table views.
 *
 * Caps how many rows are rendered into the DOM at once, which keeps large
 * lists (e.g. the History view's full completed window) from blocking the main
 * thread on first paint. Views that pass a small list are unaffected.
 *
 * The current page is clamped to the available range and resets to the first
 * page whenever the underlying row count shrinks below the current offset
 * (e.g. after a filter narrows the data).
 */
import { useMemo, useState } from 'react';

export interface Pagination<T> {
  /** The rows for the current page only. */
  pageItems: T[];
  /** Zero-based index of the current page. */
  pageIndex: number;
  /** Total number of pages (at least 1). */
  pageCount: number;
  /** 1-based index of the first item shown on the current page. */
  rangeStart: number;
  /** 1-based index of the last item shown on the current page. */
  rangeEnd: number;
  /** Total item count across all pages. */
  totalItems: number;
  goToPage: (index: number) => void;
  nextPage: () => void;
  prevPage: () => void;
}

/**
 * Slices a list into pages of `pageSize` and tracks the active page.
 */
export function usePagination<T>(items: T[], pageSize: number): Pagination<T> {
  const [pageIndex, setPageIndex] = useState<number>(0);

  const totalItems = items.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  // Clamp in case the data shrank (e.g. a filter was applied) below the offset.
  const safeIndex = Math.min(pageIndex, pageCount - 1);

  const pageItems = useMemo(() => {
    const start = safeIndex * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safeIndex, pageSize]);

  const goToPage = (index: number): void => {
    setPageIndex(Math.max(0, Math.min(index, pageCount - 1)));
  };

  const nextPage = (): void => goToPage(safeIndex + 1);
  const prevPage = (): void => goToPage(safeIndex - 1);

  const rangeStart = totalItems === 0 ? 0 : safeIndex * pageSize + 1;
  const rangeEnd = Math.min(totalItems, (safeIndex + 1) * pageSize);

  return {
    pageItems,
    pageIndex: safeIndex,
    pageCount,
    rangeStart,
    rangeEnd,
    totalItems,
    goToPage,
    nextPage,
    prevPage,
  };
}
